import React, { useEffect, useRef, useState } from 'react';
import './contact-us-form.styles.scss';
import { useLocation, useNavigate } from 'react-router-dom';
import ThreeDotsLoader from '@/components/loader/three-dots-loader.component';
import { UseCloudFunction } from '@/utils/firebase.utils';
import { useSelector } from 'react-redux';
import { ModalAlert, ModalConfirm } from '@/components/modal/modal.component';
import { resizeImage } from '@/utils/resizeImage';
import { getDownloadURL, getStorage, ref, uploadBytes } from 'firebase/storage';
import useAnalytics from '@/utils/useAnalytics';

const ContactUsForm = ({ tall, cancel }) => {

  const currentUser = useSelector(state => state.userData.currentUser);
  const userProfile = useSelector(state => state.userData.userProfile);
  const membership = useSelector(state => state.userData.membership);
  const [formData, setFormData] = useState({});
  const [readyToSend, setReadyToSend] = useState(false);
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);
  const [alertMessage, setAlertMessage] = useState(null);
  const [confirmMessage, setConfirmMessage] = useState(null);
  const [imageUploaded, setImageUploaded] = useState(false);
  const { trackEvent, trackException } = useAnalytics();
  const dataRef = useRef();
  const approveRef = useRef();
  const navigate = useNavigate();
  const location = useLocation();
  const storage = getStorage();

  useEffect(() => {
    if (!userProfile) return;
    dataRef.current = {
      ...dataRef.current,
      ...{
        'fName': userProfile.fName,
        'lName': userProfile.lName,
        'email': userProfile.email,
      }
    }
    setFormData(dataRef.current);
  }, [userProfile])

  useEffect(() => {
    if (!formData) return;
    if (
      formData.fName &&
      formData.lName &&
      formData.email &&
      formData.purpose &&
      formData.message
    ) {
      setReadyToSend(true);
    } else {
      setReadyToSend(false);
    }
  }, [formData])

  function saveData(key, value) {
    dataRef.current = { ...dataRef.current, ...{ [key]: value } };
    setFormData(dataRef.current);
  }

  function clickUpload() {
    document.getElementById('contact-image-upload').value = null;
    document.getElementById('contact-image-upload').click();
  }

  function handleUpload(file) {
    console.log(file);
    if (file.size > 2 * 1024 * 1024) {
      setAlertMessage('That file is too large to be uploaded. ' +
        'You cannot upload files larger than 2MB.');
    }
    setImageUploaded({
      file: file,
      name: file.name
    });
  }

  function sendContact() {
    if (location.pathname.includes('/preview/manager/')) {
          setAlertMessage('Sorry! Data cannot be saved in Preview Mode.');
          return;
      }
    setConfirmMessage('Are you ready to send this message? ' +
      'We will get back to you as soon as we can!');
    approveRef.current = () => {
      continueSendContact();
    }

    async function continueSendContact() {
      
      setLoading(true);
      const tempData = { ...dataRef.current };
      if (imageUploaded) {
        const file = document.getElementById('contact-image-upload').files[0]
        let newURL = '';
        const now = new Date().getTime();
        if (file) {
          console.log(file.size)
          const resizedImage = await resizeImage(file)
          console.log(resizedImage.size)
          const contactStorageRef = ref(storage, `uploaded-contact-images/${now}-${file.name}`)
          const metadata = { contentType: resizedImage.type }
          await uploadBytes(contactStorageRef, resizedImage, metadata)
            .then(snap => console.log(snap))
          newURL = await getDownloadURL(contactStorageRef)
          console.log(newURL)
          tempData.imageURL = newURL;
          tempData.imageName = file.name;
        }

      }
      if (userProfile) {
        tempData.appId = userProfile.appId;
        tempData.userEmail = userProfile.email;
      }
      const res = await UseCloudFunction(
        'submitContactUs', 
        { 
          'contactData': tempData 
        }
      )
      console.log(res);
      setLoading(false);
      if (res.error) {
        setAlertMessage(`Sorry, something went wrong. ` + 
          `Please try again later. (Error: ${res.error})`);
        trackException(`Contact Us Submit Error: ${res.error}`);
        return;
      }
      trackEvent(
        'contact_us_submitted',
        'User',
        `User ${userProfile
          ? userProfile.appId
          : 'Guest'
        } submitted a contact us form.`,
        null,
        membership.membership
      )
      setSent(true);
    }
  }

  return (
    <div className='contact-us-form'>
      {
        (sent)
          ?
          <div className='center-col'>
            <h2>Thank you for reaching out!</h2>
            <p>Someone from Gamably will be in touch with you very soon!</p>
          </div>
          :
          <div className='fields'>
            <div className={`two-inputs ${tall ? 'tall' : ''}`}>
              <div className='field small'>
                <label>First Name</label>
                <input
                  type='text'
                  className={userProfile ? 'disabled' : ''}
                  value={formData.fName ?? ''}
                  onChange={e => saveData('fName', e.target.value)} />
              </div>
              <div className='field small'>
                <label>Last Name</label>
                <input
                  type='text'
                  className={userProfile ? 'disabled' : ''}
                  value={formData.lName ?? ''}
                  onChange={e => saveData('lName', e.target.value)} />
              </div>
            </div>
            <div className={`two-inputs ${tall ? 'tall' : ''}`}>
              <div className='field small'>
                <label>Email</label>
                <input
                  type='text'
                  className={userProfile ? 'disabled' : ''}
                  value={formData.email ?? ''}
                  onChange={e => saveData('email', e.target.value)} />
              </div>
            </div>
            <div className='field small'>
              <label>Reason for Contacting Us</label>
              <select
                value={formData.purpose ?? ''}
                onChange={e => saveData('purpose', e.target.value)} >
                <option value=''>Select one...</option>
                <option value='quote'>I would like an Enterprise Quote</option>
                <option value='feature'>I have a feature idea</option>
                <option value='problem'>I have a problem I'd like to report</option>
                <option value='hey'>Just saying hey!</option>
              </select>
            </div>
            <div className='field small'>
              <label>How can we help?</label>
              <textarea
                rows='3'
                value={formData.message ?? ''}
                onChange={e => saveData('message', e.target.value)}>
              </textarea>
            </div>
            {
              (formData.purpose && formData.purpose !== 'quote') &&
              <div className='upload'>
                <div style={{ cursor: 'pointer' }}>
                  <input
                    type="file"
                    id="contact-image-upload"
                    accept="image/*"
                    onChange={(e) => handleUpload(e.target.files[0])} />
                  <button
                    className="g-button small-btn"
                    type="button"
                    tabIndex="-1"
                    onClick={() => clickUpload()}>
                    Upload an Image
                  </button>
                </div>
                <div>
                  {
                    (!imageUploaded || !imageUploaded.name)
                      ?
                      <p>Nothing uploaded yet...</p>
                      :
                      <div>{
                        imageUploaded.name.length > 30
                          ?
                          imageUploaded.name.substring(0, 30) + '...'
                          :
                          imageUploaded.name
                      }</div>
                  }
                </div>
              </div>
            }
            {
              (loading)
                ?
                <div className='buttons'>
                  <button className='g-button disabled'>
                    Cancel
                  </button>
                  <button className='g-button disabled'>
                    <ThreeDotsLoader />
                  </button>
                </div>
                :
                (readyToSend)
                  ?
                  <div className='buttons'>
                    <button
                      className='g-button'
                      onClick={() => cancel()}>
                      Cancel
                    </button>
                    <button
                      className='g-button primary'
                      onClick={() => sendContact()}>
                      Send Message
                    </button>
                  </div>
                  :
                  <div className='buttons'>
                    <button
                      className='g-button'
                      onClick={() => cancel()}>
                      Cancel
                    </button>
                    <button className='g-button disabled primary'>
                      Send Message
                    </button>
                  </div>
            }
          </div>
      }
      <ModalAlert
        show={alertMessage}
        cancel={() => setAlertMessage(null)}
        message={alertMessage}
        full={true} />
      <ModalConfirm
        show={confirmMessage}
        cancel={() => setConfirmMessage(null)}
        message={confirmMessage}
        onApprove={approveRef.current}
        full={true} />
    </div>
  )
}

export default ContactUsForm